import React, { useEffect, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, CheckCircle2, XCircle, Loader2, Hash, Calendar, FileText, Award } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const initial = { loading: true, data: null, error: null };
function reducer(state, action) {
  switch (action.type) {
    case 'SUCCESS': return { loading: false, data: action.payload, error: null };
    case 'FAILURE': return { loading: false, data: null, error: action.payload };
    default: return state;
  }
}

/**
 * Página PÚBLICA (sem login) — verificação independente de Dossiê Executivo.
 * URL: /verify-dossier/:dossierId
 */
export default function VerifyDossierPage() {
  const { dossierId } = useParams();
  const [state, dispatch] = useReducer(reducer, initial);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/verify-dossier/${dossierId}`)
      .then((res) => { if (!cancelled) dispatch({ type: 'SUCCESS', payload: res.data }); })
      .catch((e) => { if (!cancelled) dispatch({ type: 'FAILURE', payload: e?.response?.data?.detail || 'Erro ao verificar' }); });
    return () => { cancelled = true; };
  }, [dossierId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4" data-testid="verify-page">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 mb-4">
            <Shield className="w-8 h-8 text-blue-300" />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Verificação de Dossiê</h1>
          <p className="text-blue-200 mt-2">BlueGov — Plataforma de Conformidade e Proteção Institucional</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl bg-white shadow-2xl overflow-hidden" data-testid="verify-card">
          {state.loading && (
            <div className="p-12 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p>Validando dossiê institucional…</p>
            </div>
          )}

          {!state.loading && (!state.data || !state.data.valid) && (
            <div className="p-10 flex flex-col items-center text-center gap-3" data-testid="verify-invalid">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-9 h-9 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-700">Dossiê não encontrado</h2>
              <p className="text-slate-600 max-w-md">
                {state.error || state.data?.reason || 'O identificador fornecido não corresponde a nenhum dossiê emitido pelo BlueGov.'}
              </p>
              <p className="text-xs text-slate-400 mt-2 font-mono">{dossierId}</p>
            </div>
          )}

          {!state.loading && state.data?.valid && (
            <div data-testid="verify-valid">
              {/* Valid banner */}
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-5 flex items-center gap-3">
                <CheckCircle2 className="w-7 h-7" />
                <div>
                  <p className="text-lg font-extrabold">Dossiê válido e autêntico</p>
                  <p className="text-emerald-50 text-sm">Emitido pelo BlueGov e registrado em base institucional</p>
                </div>
              </div>

              <div className="p-6 md:p-8 space-y-4">
                <Field icon={FileText} label="ID do Dossiê" value={state.data.dossier_id} mono />
                <Field icon={FileText} label="Ato de Controle" value={state.data.act_id} mono />
                <Field icon={Calendar} label="Emitido em" value={new Date(state.data.created_at).toLocaleString('pt-BR')} />
                <Field icon={Award} label="Score Institucional" value={`${state.data.score} / 100`} highlight />
                <Field icon={Hash} label="Hash SHA-256 do PDF" value={state.data.hash} mono small />
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Atestado de integridade</p>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    {state.data.verification}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>Tenant: <strong className="text-slate-700">{state.data.tenant_id}</strong></span>
                <span>{(state.data.pdf_size_bytes / 1024).toFixed(1)} KB</span>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-blue-200/60 text-xs mt-6">
          BlueGov · Plataforma de Conformidade e Proteção Institucional
        </p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, mono = false, small = false, highlight = false }) {
  return (
    <div className="flex gap-3">
      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</p>
        <p className={`${mono ? 'font-mono' : ''} ${small ? 'text-[11px] break-all' : 'text-sm'} ${highlight ? 'text-2xl font-extrabold text-emerald-700' : 'text-slate-900 font-semibold'} mt-0.5`}>
          {value}
        </p>
      </div>
    </div>
  );
}
